// WHERE IS THIS UNASSIGNED DEVICE? — traced from the network it is on.
//
// Asked for 2026-10-24 (the owner), from a console line nobody could act on:
//
//   12:38:51 ← 907612/client: WS,1544,56,2 Touch Switch Tuya,…,Optilink,
//                             192.168.10.38,{0,0,0},9600 Unassigned - Smartify_907612
//
// An unassigned board says nothing about its owner — but it does say which
// network it joined, and the devices that ARE assigned on that same network
// name the house. 907612 traced to Nikhil Kulkarni, location 37 "Home",
// Badlapur: 9 of his 13 devices are on "Optilink" / 192.168.10.x, and two of
// them report the very same access point (`Bssid B4:F9:49:84:15:65`).
//
// So the Message Box can say what it thinks — "probably installed at Home ·
// Badlapur (Nikhil Kulkarni), and it is a 2 Touch Switch" — and the operator
// assigns it instead of wondering what the number is. That is the whole point:
// every unidentified device gets a sentence a human can act on.
//
// ── THREE SIGNALS, IN ORDER OF STRENGTH ───────────────────────────────────
//   bssid   the same ACCESS POINT. One physical router: conclusive for a site.
//   subnet  the same SSID **and** the same /24. A name plus an address range.
//   ssid    the same SSID only. Weakest, and sometimes worthless (below).
//
// ⚠️ A FACTORY SSID NAMES NOTHING. Four of Nikhil's own devices still report
// "Smartify" — the firmware's own default — on three unrelated subnets, and
// "Home" is the SSID of dozens of real houses. An SSID that appears at MANY
// locations is therefore evidence of nothing, and this file never hardcodes a
// blacklist: it counts how many locations share the name and says "weak" when
// the data says so. The operator sees the count, not just a verdict.
//
// ⚠️ IT CANNOT TELL YOU THE ROOM, and must not pretend to. The rooms of the
// matching devices are listed as a hint for whoever is standing there; only the
// installer, or someone pressing the switch, knows which one it is.
//
// Pure — rows in, a guess out. The rows are `GetDeviceTrackingList`'s (one
// reply for the estate, which now carries WifiName / DeviceIp / Bssid).

const str = (v) => String(v ?? '').trim();
const lower = (v) => str(v).toLowerCase();
const num = (v) => Number(v) || 0;

/** 192.168.10.38 -> "192.168.10". '' when it is not a dotted quad. */
export function subnetOf(ip) {
  const m = /^(\d{1,3}\.\d{1,3}\.\d{1,3})\.\d{1,3}$/.exec(str(ip));
  return m ? m[1] : '';
}

/**
 * Is this row an UNASSIGNED device — one no app can see?
 * The three facts the backend's own `Unassigned - …` caption is built from:
 * no owner, no location, and no active user mapping.
 */
export const isUnassignedRow = (row) => !!row
  && num(row.MasterUserId) === 0
  && num(row.LocationID) === 0
  && num(row.MappedUsers) === 0;

// An SSID seen at more than this many locations tells you nothing about which
// house you are in ("Smartify", "Home"). Measured: Nikhil's own factory-default
// devices sit on "Smartify" across three subnets.
export const GENERIC_SSID_LOCATIONS = 3;

/**
 * Group the estate's rows by the three signals, once, so a console that is
 * naming many devices does not re-scan 1800 rows per device.
 * -> { byBssid, bySsid, byKey (ssid|subnet), ssidLocations }
 */
export function buildNetworkIndex(rows) {
  const byBssid = new Map();
  const bySsid = new Map();
  const byKey = new Map();
  const ssidLocations = new Map();
  const push = (map, key, row) => {
    if (!key) return;
    const list = map.get(key);
    if (list) list.push(row); else map.set(key, [row]);
  };
  (rows || []).forEach((row) => {
    // Only a device that IS placed can name a place.
    if (num(row.LocationID) === 0) return;
    const ssid = lower(row.WifiName);
    const sub = subnetOf(row.DeviceIp);
    push(byBssid, lower(row.Bssid), row);
    push(bySsid, ssid, row);
    if (ssid && sub) push(byKey, `${ssid}|${sub}`, row);
    if (ssid) {
      const set = ssidLocations.get(ssid) || new Set();
      set.add(num(row.LocationID));
      ssidLocations.set(ssid, set);
    }
  });
  return { byBssid, bySsid, byKey, ssidLocations };
}

const CONFIDENCE = { bssid: 'high', subnet: 'likely', ssid: 'weak' };

/**
 * Who shares this device's network, grouped by location and ranked.
 *
 * `subject` is the device's own tracking row (or anything carrying WifiName /
 * DeviceIp / Bssid). -> [{ locationId, locationName, ownerName, ownerUserId,
 * devices, rooms, signal, confidence, ssid, subnet, ssidLocations }]
 */
export function siteCandidates(subject, index) {
  if (!subject || !index) return [];
  const ssid = lower(subject.WifiName);
  const sub = subnetOf(subject.DeviceIp);
  const bssid = lower(subject.Bssid);
  const me = str(subject.UniqueNumber);

  // The strongest signal that has any matches at all wins — never a mix, so the
  // sentence can say exactly WHY.
  const tries = [
    ['bssid', bssid ? index.byBssid.get(bssid) : null],
    ['subnet', ssid && sub ? index.byKey.get(`${ssid}|${sub}`) : null],
    ['ssid', ssid ? index.bySsid.get(ssid) : null],
  ];
  const hit = tries.find(([, list]) => (list || []).some((r) => str(r.UniqueNumber) !== me));
  if (!hit) return [];
  const [signal, list] = hit;

  const spread = (index.ssidLocations.get(ssid) || new Set()).size;
  const byLocation = new Map();
  (list || []).forEach((row) => {
    if (str(row.UniqueNumber) === me) return;
    const id = num(row.LocationID);
    const got = byLocation.get(id) || {
      locationId: id,
      locationName: str(row.LocationName) || `Location ${id}`,
      ownerName: str(row.OwnerName),
      ownerUserId: num(row.MasterUserId),
      devices: [],
      rooms: new Set(),
    };
    got.devices.push(row);
    if (str(row.RoomName)) got.rooms.add(str(row.RoomName));
    byLocation.set(id, got);
  });

  return [...byLocation.values()]
    .map((c) => ({
      ...c,
      rooms: [...c.rooms],
      count: c.devices.length,
      signal,
      ssid,
      subnet: sub,
      ssidLocations: spread,
      // ⚠️ A name shared by many houses is not evidence, whatever the count.
      confidence: signal === 'ssid' && spread > GENERIC_SSID_LOCATIONS ? 'weak' : CONFIDENCE[signal],
    }))
    .sort((a, b) => b.count - a.count || a.locationName.localeCompare(b.locationName));
}

/**
 * The one guess, or null when the network says nothing.
 * `{ best, others, ambiguous }` — ambiguous when a second location matches the
 * same signal just as strongly, which must be SAID, not hidden behind a pick.
 */
export function guessSite(subject, index) {
  const all = siteCandidates(subject, index);
  if (!all.length) return null;
  const [best, ...others] = all;
  return {
    best,
    others,
    ambiguous: others.length > 0 && others[0].count === best.count,
  };
}

/** "2 Touch Switch" — what the device IS, from its own row. */
export const deviceKindOf = (row) => str(row?.DeviceTypeName)
  || (num(row?.DeviceID) ? `type ${num(row.DeviceID)}` : 'device');

const why = (c) => {
  const n = c.count;
  const devices = `${n} device${n === 1 ? '' : 's'}`;
  if (c.signal === 'bssid') return `${devices} there are on the same access point`;
  if (c.signal === 'subnet') return `${devices} there are on “${c.ssid}” at ${c.subnet}.x`;
  return `${devices} there are on “${c.ssid}”`;
};

/**
 * The sentence the Message Box shows. Deliberately in the first person and
 * hedged — it is an inference from the network, not a record:
 *
 *   "I think 907612 is installed at Home · Badlapur — Nikhil Kulkarni.
 *    It is a 2 Touch Switch. 9 devices there are on the same access point."
 *
 * -> { text, confidence, detail } | null
 */
export function guessSentence(subject, index) {
  const guess = guessSite(subject, index);
  if (!guess) return null;
  const { best, others, ambiguous } = guess;
  const where = [best.locationName, best.ownerName].filter(Boolean).join(' — ');
  const kind = deviceKindOf(subject);
  const bits = [`I think ${str(subject.UniqueNumber)} is installed at ${where}.`];
  bits.push(`It is a ${kind}.`);
  bits.push(`${why(best)}.`);
  if (best.rooms.length) {
    bits.push(`Their rooms: ${best.rooms.slice(0, 4).join(', ')}${best.rooms.length > 4 ? '…' : ''} — which one this is still needs someone to press it.`);
  }
  if (ambiguous) bits.push(`⚠️ ${others[0].locationName} matches just as well — do not assign on this alone.`);
  if (best.confidence === 'weak') {
    bits.push(`⚠️ “${best.ssid}” is in use at ${best.ssidLocations} locations, so the name alone proves nothing.`);
  }
  return { text: bits.join(' '), confidence: best.confidence, detail: best };
}

// ── The device's own boot line is also a source ───────────────────────────
// ⚠️ THE LINE CARRIES THE SSID AND THE IP, and the DevicesInstalled row does
// not always (the owner pointed at exactly this: "in this we are getting wifi
// ssid, that is Optilink"). It is also FRESHER than the row, and it exists for
// a board that has no row at all. So a guess prefers the line and falls back to
// the row.
//
//   WS,1544,56,2 Touch Switch Tuya,2026-10-05T16:36:19.000Z,Optilink,
//      192.168.10.38,{0,0,0},9600 Unassigned - Smartify_907612
//   \_ 0  \_1 \_2 \_ 3                \_ 4 (built)        \_ 5      \_ 6
//
// ⚠️ THE TYPE NAME CAN CONTAIN SPACES BUT NEVER A COMMA, and the trailing field
// is "<baud> <free text>" — so this splits on commas and never on spaces.
// Anything that is not a WS line returns null rather than a half-filled object.
export function parseWsLine(payload) {
  const body = str(payload).replace(/^\[[^\]]*\]/, '').trim();   // an optional "[uno]" prefix
  if (!/^WS\s*,/i.test(body)) return null;
  const f = body.split(',').map((x) => str(x));
  if (f.length < 7) return null;
  const ip = f[6];
  return {
    ID: num(f[1]) || 0,
    DeviceID: num(f[2]) || 0,
    DeviceTypeName: f[3] || '',
    BuiltAt: f[4] || '',
    WifiName: f[5] || '',
    DeviceIp: /^\d{1,3}(\.\d{1,3}){3}$/.test(ip) ? ip : '',
    // "Unassigned - Smartify_907612" is the firmware's own word for it.
    SaysUnassigned: /unassigned/i.test(body),
  };
}

/**
 * What to trace: the live line where it has something, the stored row where it
 * does not. Either may be missing entirely.
 */
export function subjectFrom({ uno, row, payload } = {}) {
  const line = parseWsLine(payload);
  if (!row && !line) return null;
  return {
    ...(row || {}),
    UniqueNumber: str(uno) || str(row && row.UniqueNumber),
    WifiName: str(line && line.WifiName) || str(row && row.WifiName),
    DeviceIp: str(line && line.DeviceIp) || str(row && row.DeviceIp),
    Bssid: str(row && row.Bssid),                       // the line never carries it
    DeviceID: num(row && row.DeviceID) || num(line && line.DeviceID),
    DeviceTypeName: str(row && row.DeviceTypeName) || str(line && line.DeviceTypeName),
    // No row at all = nothing can have been assigned to it.
    MasterUserId: num(row && row.MasterUserId),
    LocationID: num(row && row.LocationID),
    MappedUsers: num(row && row.MappedUsers),
  };
}

/** The unique numbers a device has spoken on, newest first. Pure. */
export function unosHeard(messages, { limit = 12, dir = 'in' } = {}) {
  const out = [];
  (messages || []).forEach((m) => {
    const uno = str(m && m.uno);
    // `dir` is mqttMonitor.DIR_FROM_DEVICE ('in') — only the DEVICE speaking
    // counts; our own publishes to it prove nothing about where it is.
    if (!uno || (dir && m.dir !== dir)) return;
    if (!out.includes(uno)) out.push(uno);
  });
  return out.slice(0, limit);
}

const siteGuess = {
  subnetOf, isUnassignedRow, buildNetworkIndex, siteCandidates, guessSite, guessSentence,
  deviceKindOf, parseWsLine, subjectFrom, unosHeard,
};
export default siteGuess;
